import type { ExploreSection } from "@/hooks/useExploreSections";
import { MobileExploreCard } from "./MobileExploreCard";
import type { ExploreResultItem } from "@/types/exploreResult";
import { itemKey } from "@/hooks/useExploreResults";

interface ExploreSectionListProps {
  sections: ExploreSection[];
  onCardClick?: (item: ExploreResultItem, sectionKey: string, index: number) => void;
}

/**
 * Renders Explore sections with headers and horizontal scrollers
 * for curated sections, vertical list for "Alla".
 * Empty sections (0 items) are hidden automatically.
 *
 * See docs/design-rules/ux-rules/explore-12-10-sot.md §3
 */
export function ExploreSectionList({ sections, onCardClick }: ExploreSectionListProps) {
  // Filter out empty sections
  const visibleSections = sections.filter((s) => s.items.length > 0);

  return (
    <div className="pb-4">
      {visibleSections.map((section, sIdx) => {
        const isAllSection = section.key === "all";
        const isCurated = !isAllSection;

        return (
          <div key={section.key}>
            {/* Section header */}
            <div className="px-4 pt-6 pb-3">
              <h2 className="text-lg font-semibold text-foreground leading-tight">
                {section.title}
              </h2>
              {section.subtitle && (
                <p className="text-sm text-muted-foreground mt-0.5">
                  {section.subtitle}
                </p>
              )}
            </div>

            {/* Curated sections: horizontal scroller */}
            {isCurated ? (
              <div
                className="flex gap-3 overflow-x-auto scrollbar-none px-4 pb-2"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                {section.items.map((item, idx) => (
                  <div
                    key={itemKey(item)}
                    className="shrink-0 w-[280px]"
                    onClick={() => onCardClick?.(item, section.key, idx)}
                  >
                    <MobileExploreCard item={item} />
                  </div>
                ))}
              </div>
            ) : (
              /* "Alla" section: vertical list (List = Go) */
              <div className="divide-y divide-border">
                {section.items.map((item, idx) => (
                  <div
                    key={itemKey(item)}
                    onClick={() => onCardClick?.(item, section.key, idx)}
                  >
                    <MobileExploreCard item={item} />
                  </div>
                ))}
              </div>
            )}

            {/* Separator between curated sections */}
            {isCurated && sIdx < visibleSections.length - 1 && (
              <div className="border-b border-border/40 mx-4" />
            )}
          </div>
        );
      })}
    </div>
  );
}
